import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, ShieldCheck, ScanSearch, FileCheck2, Gauge, ClipboardCheck, Radio } from "lucide-react";
import ScanCanvas from "@/components/ScanCanvas";
import Marquee from "@/components/Marquee";
import SpotlightCard from "@/components/SpotlightCard";
import CTABanner from "@/components/CTABanner";
import { Reveal, Eyebrow, MaskLines } from "@/components/Reveal";

const HERO_IMG = "https://images.unsplash.com/photo-1716031112197-db7e3e1e232e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODh8MHwxfHNlYXJjaHwzfHxpbmR1c3RyaWFsJTIwaW5zcGVjdG9yJTIwcGlwZWxpbmUlMjByZWZpbmVyeSUyMHdlbGRpbmclMjBoaWdoJTIwcXVhbGl0eXxlbnwwfHx8fDE3ODk1MDU0MjV8MA&ixlib=rb-4.1.0&q=85";
const SPOTLIGHT_IMG = "https://images.unsplash.com/photo-1507497806295-753c4108560c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODh8MHwxfHNlYXJjaHwxfHxpbmR1c3RyaWFsJTIwaW5zcGVjdG9yJTIwcGlwZWxpbmUlMjByZWZpbmVyeSUyMHdlbGRpbmclMjBoaWdoJTIwcXVhbGl0eXxlbnwwfHx8fDE3ODk1MDU0MjV8MA&ixlib=rb-4.1.0&q=85";

const stats = [
  { value: "III", label: "ASNT Level III Led Auditing" },
  { value: "06+", label: "NDE Methods Under One Roof" },
  { value: "100%", label: "Documentation Traceability" },
  { value: "24h", label: "Rapid Mobilization Response" },
];

const services = [
  { icon: ScanSearch, code: "SRV-01", title: "NDE Auditing", desc: "ASNT Level III–led audits of NDE programs, personnel, and execution — independent, defensible, code-aligned." },
  { icon: Radio, code: "SRV-02", title: "UT & PAUT", desc: "Ultrasonic and phased array testing oversight for welds, piping, vessels, and critical structural components." },
  { icon: Gauge, code: "SRV-03", title: "VT / MT / PT", desc: "Visual, magnetic particle, and liquid penetrant inspection with disciplined technique and reporting." },
  { icon: FileCheck2, code: "SRV-04", title: "Film Auditing", desc: "Radiographic film review and auditing — second-reader verification you can stand behind." },
  { icon: ClipboardCheck, code: "SRV-05", title: "QA/QC & Compliance", desc: "Procedure verification, compliance review, and documentation control against ASME, API, AWS, and ISO." },
  { icon: ShieldCheck, code: "SRV-06", title: "Field & Project Support", desc: "Field inspection, contractor oversight, client representation, tracking, and field reporting." },
];

const industries = [
  { num: "01", name: "Industrial Facilities" },
  { num: "02", name: "Pipeline Operations" },
  { num: "03", name: "Utility Infrastructure" },
  { num: "04", name: "Energy & Construction" },
  { num: "05", name: "Maintenance & Turnaround" },
];

const reasons = [
  { num: "01", title: "Level III Authority", desc: "Every audit and program review is led by ASNT Level III expertise — not delegated, not diluted." },
  { num: "02", title: "Independent Eyes", desc: "APX works for the owner. Our findings are objective, documented, and defensible." },
  { num: "03", title: "Field-Tested Judgment", desc: "Decades of real plant, pipeline, and turnaround experience behind every call we make." },
  { num: "04", title: "Responsive Execution", desc: "Projects do not wait. We mobilize fast and keep your schedule intact." },
  { num: "05", title: "Documentation That Holds Up", desc: "Reports, records, and traceability built to survive any audit — internal or regulatory." },
  { num: "06", title: "Zero-Defect Mindset", desc: "We treat every weld, every report, every signature as if the asset depends on it. Because it does." },
];

export default function Home() {
  useEffect(() => {
    document.title = "APX Project Solutions LLC | NDE Auditing, Inspection & QA/QC";
  }, []);

  const { scrollY } = useScroll();
  const heroY = useTransform(scrollY, [0, 600], [0, 120]);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative flex min-h-[calc(100vh-72px)] items-center overflow-hidden" data-testid="home-hero">
        <ScanCanvas className="absolute inset-0 h-full w-full" />
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-ink/40 via-transparent to-ink" />
        <div className="relative mx-auto w-full max-w-7xl px-5 pb-24 pt-20 sm:px-8">
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8, delay: 0.1 }}>
            <Eyebrow>APX Project Solutions LLC — NDE Auditing by Level III</Eyebrow>
          </motion.div>
          <h1 className="mt-8 font-display text-6xl font-black uppercase leading-[0.92] tracking-tight text-white sm:text-8xl lg:text-[9rem]" data-testid="hero-headline">
            <MaskLines
              lines={[
                <>Precision</>,
                <>you can</>,
                <span className="text-apx">prove.</span>,
              ]}
            />
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.75, ease: [0.22, 1, 0.36, 1] }}
            className="mt-8 max-w-xl text-base leading-relaxed text-zinc-400 sm:text-lg"
          >
            ASNT Level III–led NDE auditing, inspection, and QA/QC support for industrial facilities, pipelines, utilities, energy projects, and turnarounds — where certainty is not optional.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="mt-10 flex flex-wrap gap-4"
          >
            <a
              href="mailto:APX@apxproject.com?subject=Project%20Inquiry%20—%20APX%20Project%20Solutions"
              data-testid="hero-email-cta"
              className="chamfer-sm group inline-flex items-center gap-3 bg-apx px-7 py-4 font-mono text-sm font-bold uppercase tracking-[0.15em] text-ink transition-colors hover:bg-white"
            >
              Email APX@apxproject.com
              <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </a>
            <Link
              to="/services"
              data-testid="hero-services-link"
              className="chamfer-sm inline-flex items-center gap-2 border border-white/20 px-7 py-4 font-mono text-sm font-bold uppercase tracking-[0.15em] text-white transition-colors hover:border-apx hover:text-apx"
            >
              Explore Services
            </Link>
          </motion.div>
        </div>
        <motion.div
          style={{ y: heroY }}
          className="chamfer absolute bottom-10 right-5 hidden w-[380px] overflow-hidden border border-white/10 lg:block xl:right-16"
          data-testid="hero-image-frame"
        >
          <img src={HERO_IMG} alt="Industrial welding inspection on pipeline" className="h-64 w-full object-cover" />
          <div className="flex items-center justify-between bg-coal px-4 py-3">
            <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">Field Verification</span>
            <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-apx">Live Scan</span>
          </div>
        </motion.div>
      </section>

      {/* STATS */}
      <section className="border-y border-white/[0.08] bg-coal" data-testid="home-stats">
        <div className="mx-auto grid max-w-7xl grid-cols-2 divide-white/[0.08] px-5 sm:px-8 lg:grid-cols-4 lg:divide-x">
          {stats.map((s, i) => (
            <Reveal key={s.label} delay={i * 0.08} className="px-2 py-10 lg:px-8">
              <p className="font-display text-5xl font-black text-apx">{s.value}</p>
              <p className="mt-2 font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-400">{s.label}</p>
            </Reveal>
          ))}
        </div>
      </section>

      <Marquee />

      {/* SERVICES OVERVIEW */}
      <section className="mx-auto max-w-7xl px-5 py-24 sm:px-8 sm:py-32" data-testid="home-services-overview">
        <Reveal>
          <Eyebrow>Service Overview</Eyebrow>
          <div className="mt-6 flex flex-col justify-between gap-6 lg:flex-row lg:items-end">
            <h2 className="max-w-2xl font-display text-4xl font-black uppercase leading-none tracking-tight text-white sm:text-6xl">
              Full-spectrum NDE &amp; quality command
            </h2>
            <Link to="/services" data-testid="services-overview-link" className="group inline-flex w-fit items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-zinc-400 transition-colors hover:text-apx">
              All services <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </div>
        </Reveal>
        <div className="mt-14 grid gap-px overflow-hidden border border-white/[0.08] bg-white/[0.08] sm:grid-cols-2 lg:grid-cols-3">
          {services.map((s, i) => (
            <Reveal key={s.code} delay={(i % 3) * 0.08}>
              <SpotlightCard className="group h-full bg-ink p-8 transition-colors hover:bg-surface" data-testid={`service-card-${i}`}>
                <div className="flex items-start justify-between">
                  <s.icon className="h-7 w-7 text-apx" strokeWidth={1.5} />
                  <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-600">{s.code}</span>
                </div>
                <h3 className="mt-8 font-display text-2xl font-bold uppercase tracking-wide text-white">{s.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-zinc-400">{s.desc}</p>
              </SpotlightCard>
            </Reveal>
          ))}
        </div>
      </section>

      {/* INDUSTRIES */}
      <section className="border-y border-white/[0.08] bg-coal py-24 sm:py-32" data-testid="home-industries">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal>
            <Eyebrow>Industries Served</Eyebrow>
            <h2 className="mt-6 max-w-2xl font-display text-4xl font-black uppercase leading-none tracking-tight text-white sm:text-6xl">
              Where failure is not an option
            </h2>
          </Reveal>
          <div className="mt-14">
            {industries.map((ind, i) => (
              <Reveal key={ind.num} delay={i * 0.05}>
                <Link
                  to="/industries"
                  data-testid={`industry-row-${i}`}
                  className="group flex items-center justify-between border-t border-white/[0.08] py-7 transition-colors last:border-b hover:bg-white/[0.02]"
                >
                  <div className="flex items-baseline gap-6 sm:gap-10">
                    <span className="font-mono text-sm text-apx">{ind.num}</span>
                    <span className="font-display text-2xl font-bold uppercase tracking-wide text-zinc-300 transition-all group-hover:translate-x-2 group-hover:text-white sm:text-4xl">
                      {ind.name}
                    </span>
                  </div>
                  <ArrowUpRight className="h-6 w-6 shrink-0 text-zinc-600 transition-all group-hover:-translate-y-1 group-hover:translate-x-1 group-hover:text-apx" />
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* WHY APX */}
      <section className="mx-auto max-w-7xl px-5 py-24 sm:px-8 sm:py-32" data-testid="home-why-apx">
        <Reveal>
          <Eyebrow>Why Clients Choose APX</Eyebrow>
          <h2 className="mt-6 max-w-3xl font-display text-4xl font-black uppercase leading-none tracking-tight text-white sm:text-6xl">
            Chosen when the answer has to be right
          </h2>
        </Reveal>
        <div className="mt-14 grid gap-px overflow-hidden border border-white/[0.08] bg-white/[0.08] sm:grid-cols-2 lg:grid-cols-3">
          {reasons.map((r, i) => (
            <Reveal key={r.num} delay={(i % 3) * 0.08}>
              <div className="h-full bg-ink p-8" data-testid={`reason-card-${i}`}>
                <span className="font-display text-5xl font-black text-outline-orange">{r.num}</span>
                <h3 className="mt-6 font-display text-xl font-bold uppercase tracking-wide text-white">{r.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-zinc-400">{r.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* QUALITY COMMITMENT SPOTLIGHT */}
      <section className="relative overflow-hidden border-t border-white/[0.08]" data-testid="home-quality-commitment">
        <div className="mx-auto grid max-w-7xl gap-12 px-5 py-24 sm:px-8 sm:py-32 lg:grid-cols-2 lg:items-center">
          <Reveal>
            <Eyebrow>Quality Commitment</Eyebrow>
            <h2 className="mt-6 font-display text-4xl font-black uppercase leading-none tracking-tight text-white sm:text-6xl">
              The standard is the standard
            </h2>
            <p className="mt-6 max-w-lg text-base leading-relaxed text-zinc-400">
              APX holds every inspection, audit, and report to the governing code — ASME, API, AWS, ISO, and ASNT — with no shortcuts and no ambiguity. Our work is built to be re-examined: complete records, clear findings, and documentation that stands up to any second look.
            </p>
            <ul className="mt-8 space-y-4">
              {["Code-compliant procedures, verified in the field", "Independent findings, signed and traceable", "Safety and quality monitored on every engagement"].map((item) => (
                <li key={item} className="flex items-start gap-3 text-sm text-zinc-300">
                  <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-apx" />
                  {item}
                </li>
              ))}
            </ul>
            <a
              href="mailto:APX@apxproject.com?subject=Quality%20Program%20Inquiry"
              data-testid="quality-email-link"
              className="group mt-10 inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-apx transition-colors hover:text-white"
            >
              Discuss your quality program <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </a>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="chamfer relative overflow-hidden border border-white/10" data-testid="quality-spotlight-image">
              <img src={SPOTLIGHT_IMG} alt="Welding inspector at work" className="h-[420px] w-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-ink via-transparent to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 flex items-center justify-between px-5 py-4">
                <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-400">ASNT Level III</span>
                <span className="chamfer-sm bg-apx px-3 py-1 font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-ink">Verified</span>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <CTABanner />
    </div>
  );
}
